import { z } from 'zod';

export const onboardingSchema = z.object({
  // General Info
  name: z.string().min(2, 'Gym name is required'),
  shortName: z.string().min(1, 'Short name is required'),
  description: z.string().min(10, 'Description must be at least 10 characters'),
  url: z.string().url('Please enter a valid URL'),
  logo: z.string().url('Please enter a valid logo URL'),
  image: z.string().url('Please enter a valid image URL'),
  telephone: z.string().min(10, 'Phone number is required'),
  phoneDisplay: z.string().min(10, 'Phone display is required'),
  phoneTel: z.string().min(10, 'Phone tel format is required'),
  email: z.string().email('Please enter a valid email'),
  location: z.string().min(2, 'Location is required'),
  mapsEmbed: z.string()
    .optional()
    .refine(
      (val) => {
        if (!val || val === '') return true;
        // Only allow Google Maps embed iframes from trusted domains
        const allowedPatterns = [
          /^<iframe[^>]*src="https:\/\/www\.google\.com\/maps\/embed[^"]*"[^>]*><\/iframe>$/,
          /^<iframe[^>]*src="https:\/\/maps\.google\.com\/maps\/embed[^"]*"[^>]*><\/iframe>$/,
        ];
        return allowedPatterns.some(pattern => pattern.test(val.trim()));
      },
      { message: 'Only Google Maps embed iframes are allowed (e.g., <iframe src="https://www.google.com/maps/embed..." ...></iframe>)' }
    ),

  // Address
  streetAddress: z.string().min(3, 'Street address is required'),
  addressLocality: z.string().min(2, 'City is required'),
  addressRegion: z.string().min(2, 'State is required'),
  postalCode: z.string().min(5, 'Postal code is required'),
  addressCountry: z.string().default('US'),

  // Geo
  latitude: z.string().optional(),
  longitude: z.string().optional(),

  // Social
  instagram: z.string().url().or(z.string().length(0)).optional(),
  facebook: z.string().url().or(z.string().length(0)).optional(),
  youtube: z.string().url().or(z.string().length(0)).optional(),
  patreon: z.string().url().or(z.string().length(0)).optional(),

  // Arrays
  coaches: z.array(z.object({
    name: z.string().min(2, 'Coach name is required'),
    rank: z.string().min(2, 'Belt rank is required'),
    role: z.string().min(2, 'Role is required'),
    bio: z.string().min(10, 'Bio must be at least 10 characters'),
    specialties: z.array(z.string()),
    photo: z.string().url('Please enter a valid photo URL'),
  })).min(1, 'At least one coach is required'),

  values: z.array(z.object({
    title: z.string().min(2, 'Value title is required'),
    icon: z.string().min(1, 'Icon is required'),
    description: z.string().min(10, 'Description must be at least 10 characters'),
  })).min(2, 'At least two values are recommended'),

  // Schedule
  schedule: z.array(z.object({
    id: z.string().optional(),
    day: z.string().min(1, 'Day is required'),
    time: z.string().min(1, 'Time is required'),
    title: z.string().min(2, 'Class title is required'),
    level: z.string().min(1, 'Level is required'),
    type: z.enum(['Gi', 'No-Gi', 'Both']),
    ageGroup: z.enum(['Adults', 'Teens', 'Kids', 'All Ages']),
  })).min(1, 'At least one class is required'),

  // Pricing
  plans: z.array(z.object({
    id: z.string().min(1, 'Plan ID is required'),
    name: z.string().min(2, 'Plan name is required'),
    price: z.number().min(0, 'Price must be positive'),
    originalPrice: z.number().nullable().optional(),
    billing: z.enum(['month', 'week', 'class', 'year']),
    popular: z.boolean().default(false),
    features: z.array(z.string()).min(1, 'At least one feature is required'),
    description: z.string().min(10, 'Description is required'),
    checkoutLink: z.string().url().or(z.literal('')).optional().nullable(),
  })).min(1, 'At least one pricing plan is required'),

  // FAQ
  faqs: z.array(z.object({
    question: z.string().min(5, 'Question is required'),
    answer: z.string().min(10, 'Answer is required'),
  })).min(2, 'At least two FAQs are recommended'),

  // Opening Hours
  openingHours: z.array(z.object({
    dayOfWeek: z.string().min(1, 'Day(s) is required'),
    opens: z.string().min(1, 'Opening time is required'),
    closes: z.string().min(1, 'Closing time is required'),
  })).optional(),
});

export type OnboardingFormData = z.infer<typeof onboardingSchema>;

export const defaultValues: Partial<OnboardingFormData> = {
  addressCountry: 'US',
  coaches: [{ name: '', rank: '', role: '', bio: '', specialties: [], photo: '' }],
  values: [
    { title: '', icon: '📚', description: '' },
    { title: '', icon: '💪', description: '' },
  ],
  schedule: [{ day: 'Monday', time: '', title: '', level: 'All Levels', type: 'Gi', ageGroup: 'Adults' }],
  plans: [{ id: '', name: '', price: 0, billing: 'month', popular: false, features: [''], description: '' }],
  faqs: [{ question: '', answer: '' }],
  openingHours: [
    { dayOfWeek: 'Monday-Friday', opens: '11:00', closes: '21:00' },
    { dayOfWeek: 'Saturday', opens: '10:45', closes: '14:00' },
  ],
};
